import { FC, useCallback, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useDropzone } from 'react-dropzone';
import { useMyTranslations } from '@hooks/useMyTranslations';
import { useStore } from '@hooks/useStore';
import CloseIcon from '@icons/close_icon.svg?component';
import DownloadIcon from '@icons/inputs/dowload_file_icon.svg?component';
import FileIcon from '@icons/inputs/file_icon.svg?component';
import { uploadInputTranslations } from '../UploadInput/translations';
import { UploadInputTransFields } from '../UploadInput/types';
import { IUploadCSVInputProps } from './types';
import { MAX_FILE_SIZE } from './constatnts';

export const UploadCSVInput: FC<IUploadCSVInputProps> = ({ name, inputStyleProp }) => {
  const {
    alertStore: { showAlert },
  } = useStore();

  const [fileName, setFileName] = useState('');

  const { t } = useMyTranslations<UploadInputTransFields>([uploadInputTranslations.name]);

  const formContext = useFormContext();

  const {
    setValue,
    formState: { errors },
  } = formContext;

  const onDrop = useCallback(async (acceptedFiles: any) => {
    if (acceptedFiles && acceptedFiles[0]) {
      const file = acceptedFiles[0];
      if (file.type !== 'text/csv') {
        showAlert(t('incorrectFileType'), 'warning');
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        showAlert(t('incorrectFileSize'), 'warning');
        return;
      }
      setValue(`${name}`, file);
      setFileName(file.name);
    }
  }, []);

  const { getRootProps, getInputProps, open } = useDropzone({ onDrop, noClick: true });

  const getContent = useCallback(
    (value: string | undefined | null) => {
      if (value) {
        return (
          <div className="flex flex-col justify-center items-center h-full w-full p-[30px] bg-[#F3F5FC] rounded-[8px] relative">
            <FileIcon width={112} height={112} />
            <p className="text-center">{fileName}</p>
            <button
              className="absolute p-0 cursor-pointer  bg-none bottom-[16px] right-[16px] rounded-[50%] transition-all hover:bg-[rgba(white, 0.3)]"
              type="button"
              onClick={() => {
                setValue(name, null);
                setFileName('');
              }}
            >
              <CloseIcon width={16} height={16} />
            </button>
          </div>
        );
      } else {
        return (
          <div
            className={`h-full w-full rounded-[8px] bg-[rgba(246, 248, 255, 0.5)] border border-dashed border-[#C9CAD0] bg-[#F6F8FF80] flex flex-col justify-center items-center px-[12px] text-center ${
              errors[name] && 'border-error'
            }`}
          >
            <DownloadIcon width={48} height={48} />
            <p className="small-text uppercase mt-[16px]">{t('firstRow')}</p>
            <p className="small-text uppercase mt-[8px]">
              <span>{t('secondRow')}&nbsp; </span>
              <button
                type="button"
                onClick={open}
                className="cursor-pointer p-0 bg-none uppercase text-primary"
              >
                {t('btnText')}
              </button>
            </p>
            <p className="small-text opacity-40 mt-[8px]">{t('lastRow')}: CSV</p>
          </div>
        );
      }
    },
    [errors, fileName]
  );

  return (
    <div className="flex flex-col justify-center items-start h-[240px] w-full" {...getRootProps()}>
      <Controller
        control={formContext?.control}
        name={name}
        rules={{ required: true }}
        render={({ field: { value } }) => (
          <>
            <input
              {...getInputProps()}
              className={`opacity-0 w-0 h-0 overflow-hidden absolute -z-10 ${inputStyleProp}`}
              type="file"
              id={name}
              accept=".csv"
              required
            />
            {getContent(value)}
          </>
        )}
      />
    </div>
  );
};
